/**
 * 通知与公告(原型 admin/notices):机构公告(发 / 改待发 / 撤回 / 短信失败重发 / 触达)、系统通知文案、
 * 平台公告(维护 / 政策)与各端拉取的正在展示的平台公告。页面里自己建客户端,不进首屏主包。
 */
import type { ApiClient } from './client.js';

export type AudienceKind = 'all' | 'class' | 'exam' | 'contest_item' | 'teachers';
export type NoticeChannel = 'in_app' | 'parent' | 'sms';

export interface Announcement {
  id: string;
  title: string;
  content: string;
  category: string;
  audience: { kind: AudienceKind; refId?: string | null; refName?: string | null };
  channels: string[];
  status: string;
  scheduledAt: string | null;
  /** 夜间顺延的短信几点发(站内信 / 家长端已即时发出);空 = 没有待发的短信 */
  smsDueAt: string | null;
  sentAt: string | null;
  revokedAt: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  recipients: number;
  reach: number | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  platform: boolean;
}

export interface NoticeKpi {
  month: number;
  scheduled: number;
  reach: number | null;
  reachByChannel: Record<NoticeChannel, number | null>;
  smsUsed: number;
  smsQuota: number;
  smsExhausted: boolean;
  templates: number;
  templatesEditable: number;
}

export interface AudienceOption {
  id: string;
  name: string;
  count: number;
}
export interface Audiences {
  kinds: { kind: AudienceKind; label: string; count?: number }[];
  classes: AudienceOption[];
  exams: AudienceOption[];
  items: AudienceOption[];
  publicSchool: boolean;
  smsDailyLimit: number;
}

export interface ChannelStat {
  key: NoticeChannel;
  label: string;
  on: boolean;
  planned: number;
  delivered: number;
  opened: number | null;
  failed: string | null;
}
export interface AnnouncementDetail extends Announcement {
  channelStats: ChannelStat[];
  smsRetryable: boolean;
}

export interface NoticeTemplate {
  event: string;
  label: string;
  to: string;
  channels: string;
  editable: boolean;
  note: string | null;
  vars: string[];
  sample: Record<string, string | number | null>;
  defaults: { title: string; body: string };
  override: { title: string | null; body: string | null } | null;
  preview: { title: string; body: string };
  updatedAt: string | null;
}

export interface AnnouncementInput {
  title: string;
  content: string;
  category: 'transaction' | 'marketing';
  audience: { kind: AudienceKind; refId?: string | null };
  channels: NoticeChannel[];
  scheduledAt?: string | null;
}

export interface PlatformNotice extends Announcement {
  visible: boolean;
}
export interface PublicNotice {
  id: string;
  title: string;
  content: string;
  category: 'maintenance' | 'policy' | string;
  windowStart: string | null;
  windowEnd: string | null;
  sentAt: string | null;
}
export interface MaintenanceState {
  until: string | null;
  message: string | null;
  since: string;
}

export const CATEGORY_LABELS: Record<string, string> = {
  transaction: '事务提醒',
  marketing: '营销',
  maintenance: '系统维护',
  policy: '政策更新',
};
export const CHANNEL_LABELS: Record<string, string> = {
  in_app: '站内',
  parent: '家长端',
  sms: '短信',
  site: '官网',
};
/** 模板占位符的中文名(编辑文案时点一下插入) */
export const VAR_LABELS: Record<string, string> = {
  nickname: '昵称',
  seq: '第几关',
  level: '关卡名',
  got: '得分',
  total: '满分',
  teacher: '教师',
  comment: '评语',
  grade: '等级',
  score: '分数',
  badge: '徽章',
  exam: '考试',
  when: '时间',
  group: '组别',
  room: '考场',
  rule: '规则',
  contest: '赛事',
  from: '开始',
  to: '结束',
  appealEnd: '申诉截止',
  item: '赛项',
  award: '奖项',
  pack: '内容',
  amount: '金额',
  left: '剩余时间',
  number: '订单号',
  label: '周期',
  days: '学习天数',
  passed: '通关数',
  badges: '徽章数',
};

/** 占位符换成示例值(与后端同一规则:不认识的原样留着) */
export function fillPlaceholders(text: string, vars: Record<string, string | number | null>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = vars[k];
    return v === null || v === undefined ? m : String(v);
  });
}

export const STATUS_LABELS: Record<string, { text: string; tone: string }> = {
  scheduled: { text: '待发送', tone: 'brand' },
  sending: { text: '发送中', tone: 'amber' },
  sent: { text: '已发送', tone: 'ok' },
  revoked: { text: '已撤回', tone: 'bad' },
  cancelled: { text: '已取消', tone: '' },
};

export function noticesApi(api: ApiClient) {
  const b = '/v1/admin/notices';
  return {
    list: () => api.get<{ items: Announcement[]; kpi: NoticeKpi }>(b),
    audiences: () => api.get<Audiences>(`${b}/audiences`),
    detail: (id: string) => api.get<AnnouncementDetail>(`${b}/${id}`),
    create: (body: AnnouncementInput) => api.post<Announcement>(b, body),
    update: (id: string, body: AnnouncementInput) => api.put<Announcement>(`${b}/${id}`, body),
    revoke: (id: string) => api.post<Announcement>(`${b}/${id}/revoke`, {}),
    retrySms: (id: string) =>
      api.post<{ id: string; resent: number; stillFailed: number }>(`${b}/${id}/sms-retry`, {}),
    templates: () => api.get<NoticeTemplate[]>(`${b}/templates`),
    saveTemplate: (event: string, body: { title: string | null; body: string | null }) =>
      api.put<NoticeTemplate>(`${b}/templates/${event}`, body),
    resetTemplate: (event: string) => api.delete<NoticeTemplate>(`${b}/templates/${event}`),
  };
}

export function platformNoticesApi(api: ApiClient) {
  const b = '/v1/platform/notices';
  return {
    list: () => api.get<{ items: PlatformNotice[] }>(b),
    create: (body: {
      title: string;
      content: string;
      category: 'maintenance' | 'policy';
      windowStart?: string | null;
      windowEnd?: string | null;
      scheduledAt?: string | null;
    }) => api.post<Announcement>(b, body),
    revoke: (id: string) => api.post<Announcement>(`${b}/${id}/revoke`, {}),
    maintenance: () => api.get<{ maintenance: MaintenanceState | null }>('/v1/platform/maintenance'),
    startMaintenance: (body: { until?: string; message?: string }) =>
      api.put<MaintenanceState>('/v1/platform/maintenance', body),
    stopMaintenance: () => api.delete<{ ok: boolean }>('/v1/platform/maintenance'),
  };
}

/** 各端顶部横幅 / 消息中心:正在展示期的平台公告(不用登录) */
export function activePlatformNotices(api: ApiClient) {
  return api.get<{ items: PublicNotice[] }>('/v1/site/platform-notices');
}
